import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const FILTERS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "custom", label: "Custom" },
];

export default function UserChartPage() {
  const [filterType, setFilterType] = useState("week");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchChartData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${baseUrl}/auth/dashboard/user-chart`,
        {
          filterType,
          startDate: startDate ? new Date(startDate).toISOString() : null,
          endDate: endDate ? new Date(endDate).toISOString() : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data?.status && data.data) {
        const labels = data.data.map((item) => dayjs(item.date).format("DD MMM"));
        const values = data.data.map((item) => item.totalUser);
        setChartData({
          labels,
          datasets: [
            {
              label: "New Users",
              data: values,
              backgroundColor: "rgba(79, 70, 229, 0.15)",
              borderColor: "#4F46E5",
              borderWidth: 2,
              borderRadius: 6,
            },
          ],
        });
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, startDate, endDate]);

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 sm:p-6">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div>
          <h2 className="text-base font-semibold text-gray-800">User Growth</h2>
          <p className="text-xs text-gray-400 mt-0.5">New registrations over time</p>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilterType(f.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                filterType === f.value
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom date range */}
      {filterType === "custom" && (
        <div className="flex flex-wrap gap-3 mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchChartData}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700 transition"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Chart area */}
      <div className="relative">
        {loading ? (
          <div className="flex justify-center items-center h-56">
            <div className="w-8 h-8 border-4 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : chartData ? (
          <Bar
            data={chartData}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                tooltip: { mode: "index", intersect: false },
              },
              scales: {
                x: {
                  grid: { display: false },
                  ticks: { font: { size: 11 } },
                },
                y: {
                  beginAtZero: true,
                  grid: { color: "rgba(0,0,0,0.05)" },
                  ticks: { font: { size: 11 }, stepSize: 1 },
                },
              },
            }}
          />
        ) : (
          <div className="flex justify-center items-center h-56 text-gray-400 text-sm">
            No data available
          </div>
        )}
      </div>
    </div>
  );
}
